import React, { useRef, useEffect, useState } from 'react';
import { Loader2, ChevronUp, ChevronDown, AlertTriangle, Sparkles } from 'lucide-react';
import styles from './chat-agent-progress.scss';

const PipelineVisualization = ({
  stages = [],
  isActive = false,
  isComplete = false,
  thinkingStreams = {},
  error = null,
  isLightMode = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const startTimeRef = useRef(null);
  const prevIsActiveRef = useRef(isActive);
  const lastStreamTextRef = useRef(null);

  // Track elapsed time while pipeline is active
  useEffect(() => {
    if (isActive) {
      if (!startTimeRef.current) {
        startTimeRef.current = Date.now();
      }
      const interval = setInterval(() => {
        setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isActive]);

  // Trigger fade-out when active transitions to complete
  useEffect(() => {
    if (prevIsActiveRef.current && !isActive && lastStreamTextRef.current) {
      setIsFadingOut(true);
      const timer = setTimeout(() => setIsFadingOut(false), 450);
      prevIsActiveRef.current = isActive;
      return () => clearTimeout(timer);
    }
    prevIsActiveRef.current = isActive;
  }, [isActive]);

  const activeStage = stages.find(s => s.status === 'active');
  const activeStageId = activeStage?.stage || activeStage?.id;
  const streamStageId = activeStageId || Object.keys(thinkingStreams).pop();
  const streamText = thinkingStreams[streamStageId] || null;
  const allThinkingText = Object.values(thinkingStreams).filter(Boolean).join('\n\n');

  // Keep a ref to the last known stream text so:
  // 1. fade-out can still render it after pipeline completes
  // 2. between-stage transitions don't flash blank when the new stage
  //    hasn't emitted any stream chunks yet (e.g. ms_analysis → sku_analysis)
  if (streamText) lastStreamTextRef.current = streamText;
  const displayText = streamText || (isActive ? lastStreamTextRef.current : null);

  if (!isActive && !isComplete && !isFadingOut) return null;

  // FADING OUT: render the last streaming content with fade-out animation
  if (isFadingOut) {
    return (
      <div className={`${styles.pipelineContainer} ${styles.fadeOut} ${isLightMode ? styles.lightMode : ''}`}>
        <div className={styles.streamingResponse}>
          <div className={styles.streamingText}>
            {lastStreamTextRef.current}
          </div>
        </div>
      </div>
    );
  }

  // COMPLETE STATE: collapsed toggle (only show when pipeline ran this session)
  if (isComplete && !isActive) {
    if (!allThinkingText || elapsedSeconds === 0) return null;

    const label = elapsedSeconds >= 2
      ? `Thought for ${elapsedSeconds}s`
      : 'Thought for a moment';

    return (
      <div className={`${styles.thinkingToggleContainer} ${isLightMode ? styles.lightMode : ''}`}>
        <button
          className={styles.thinkingToggleBtn}
          onClick={() => setIsExpanded(prev => !prev)}
        >
          <span className={styles.thinkingToggleIcon}><Sparkles size={13} /></span>
          <span className={styles.thinkingToggleLabel}>{label}</span>
          <span className={styles.thinkingToggleChevron}>
            {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </span>
        </button>
        {isExpanded && (
          <div className={styles.thinkingExpandedContent}>
            <pre className={styles.thinkingExpandedText}>{allThinkingText}</pre>
          </div>
        )}
      </div>
    );
  }

  // ACTIVE STATE: compact status line + streaming response
  return (
    <div className={`${styles.pipelineContainer} ${isLightMode ? styles.lightMode : ''}`}>
      {/* Single-line status indicator */}
      <div className={styles.statusLine}>
        <span className={styles.statusDot} />
        {activeStage ? (
          <>
            <span className={styles.statusIcon}>
              <Loader2 size={14} className={styles.spinningIcon} />
            </span>
            <span className={styles.statusName}>
              {activeStage.name || activeStage.stage || 'Processing'}
            </span>
            {activeStage.currentAction && (
              <span className={styles.statusAction}>— {activeStage.currentAction}</span>
            )}
          </>
        ) : (
          <span className={styles.statusName}>Initializing...</span>
        )}
      </div>

      {/* Streaming response - full width, no scroll bubble */}
      {/* Use displayText so content persists between stage transitions */}
      {displayText && (
        <div className={styles.streamingResponse}>
          <div className={styles.streamingText}>
            {displayText}
            <span className={styles.streamingCursor}>▋</span>
          </div>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className={styles.pipelineError}>
          <AlertTriangle size={16} className={styles.errorIcon} />
          <span className={styles.errorMessage}>{error}</span>
        </div>
      )}
    </div>
  );
};

export default PipelineVisualization;
